import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { toast } from "sonner";
import moment from "moment-timezone";

import { AuthContext } from "../../context/AuthContextProvider";
import { BlankPreview } from "../../components/BlankPreview";
import { ReportPreview } from "../../components/ReportPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import { reportsData, requestInterpretation, useGetEcgList } from "../../api/EcgReports";
import { getItems, syncSpandanReports } from "../../api/settings";
import { endpoints, fetcherGET } from "../../utils/axios";
import CustomFilters from "../../components/Filters/Filters";
import useSendForInterpretation from "../../hooks/useSendForInterpretation";
import ReportsCardView from "./components/ReportsCardView";
import ReportsListView from "./components/ReportsListView";
import SendForInterpretationDialog from "./components/ReportsSendForInterpreation";
import { canViewAIgeneratedReports } from "../../components/Reusables/ReportCards/reportUtils";
import {
  ANALYTICS_EVENTS,
  REPORT_TYPE,
  VIEW_MODE,
  logAnalyticsEvent,
  resolveInterpreterType,
} from "../../analytics";

const ECG_REPORTS_VIEW_MODE_KEY = "ecg-reports:view-mode";
const PAGE_SIZE = 10;

const getReportId = (report) => report?.report?.id || report?.id;

const unwrapReportResponse = (response) =>
  response?.data?.data || response?.data || response || {};

const getPdfFile = (report) => ({
  url: report?.ReportPdfURL || report?.report?.ReportPdfURL,
  base64: report?.ReportPdf || report?.report?.ReportPdf,
});

const getAIReportPdfUrl = (report = {}) =>
  report?.AppreportPdfUrl ||
  report?.AppReportPdfURL ||
  report?.AppReportPdfUrl ||
  report?.appReportPdfUrl ||
  report?.appreportPdfUrl;

function EcgReportView() {
  const location = useLocation();
  const navigate = useNavigate();
  const listRef = useRef(null);
  const hashId = location.hash?.slice(1);
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const { user, all_centres, selectedCenter, onReceivePushNotification } = useContext(AuthContext);
  // Migrated businesses don't get to send reports for interpretation -
  // userType stays plain "b2b", migration is a separate flag.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;
  const { autoSendEnabled, outOfCredits: creditsExhausted, isUnlimitedCredits } = useSendForInterpretation();
  const autoSendPaused = autoSendEnabled && creditsExhausted && !isUnlimitedCredits;
  const [syncingReports, setSyncingReports] = useState(false);

  const [refreshArg, setRefreshArg] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [openedMap, setOpenedMap] = useState({});
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState(() =>
    sessionStorage.getItem(ECG_REPORTS_VIEW_MODE_KEY) === "list" ? "list" : "card"
  );
  const [args, setArgs] = useState({ page: 1, limit: PAGE_SIZE, resetToggle: false });

  const [interpretationModalOpen, setInterpretationModalOpen] = useState(false);
  const [interpretationReport, setInterpretationReport] = useState(null);
  const [interpretationItems, setInterpretationItems] = useState([]);
  const [interpretationSelected, setInterpretationSelected] = useState(null);
  const [interpretationNotes, setInterpretationNotes] = useState(null);
  const [interpretationLoading, setInterpretationLoading] = useState(false);

  const { ecgReport, isLoading, pagination } = useGetEcgList({ ...args, refreshArg });
  const reports = reportData.length > 0 ? reportData : page === 1 && Array.isArray(ecgReport) ? ecgReport : [];
  const showEmptyReports = !isLoading && Array.isArray(ecgReport) && reports.length === 0;
  const hasMore = pagination?.totalPages != null
    ? page < pagination.totalPages
    : Array.isArray(ecgReport) && ecgReport.length === PAGE_SIZE;

  useEffect(() => {
    if (!Array.isArray(ecgReport)) return;

    setReportData((previousReports) => {
      if (page === 1) return ecgReport;
      const reportIds = new Set(previousReports.map((report) => getReportId(report) || report?._id));
      return [...previousReports, ...ecgReport.filter((report) => !reportIds.has(getReportId(report) || report?._id))];
    });
  }, [ecgReport, page]);

  useEffect(() => {
    if (page === 1 && Number.isFinite(Number(pagination?.total))) {
      setTotalCount(Number(pagination.total));
    }
  }, [page, pagination?.total]);

  useEffect(() => {
    if (onReceivePushNotification) setRefreshArg((current) => !current);
  }, [onReceivePushNotification]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await getItems();
        if (res?.success && Array.isArray(res.data)) {
          setInterpretationItems(
            res.data.map((item, index) => ({
              value: item.id || `item-${index}`,
              label: item.name.trim(),
              userType: item.userType || "unknown",
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    fetchItems();
  }, []);

  useEffect(() => {
    if (!hashId || !reports.length) return;
    const matchedReport = reports.find((report) => String(getReportId(report)) === String(hashId));
    if (matchedReport) setSelectedReport(matchedReport);
  }, [hashId, reports]);

  const applyFilters = (nextFilters) => {
    setPage(1);
    setReportData([]);
    setArgs({
      page: 1,
      limit: PAGE_SIZE,
      searchText: nextFilters.searchText || "",
      status: (nextFilters.status || []).join(","),
      testResult: (nextFilters.testResult || []).join(","),
      assignedTo: (nextFilters.assignees || []).join(","),
      platform: (nextFilters.platform || []).join(","),
      dateFrom: nextFilters.dateFrom || "",
      dateTo: nextFilters.dateTo || "",
      centerIds: JSON.stringify(nextFilters.spokeIds?.length ? nextFilters.spokeIds : nextFilters.hubIds || []),
      sorting: nextFilters.sort || "desc",
    });
  };

  const handleSearch = (searchText) => {
    setPage(1);
    setReportData([]);
    setArgs((currentArgs) => ({ ...currentArgs, page: 1, limit: PAGE_SIZE, searchText }));
  };

  // Lets a migrated user re-sync reports from their old Spandan account at any
  // time, not just the one-shot onboarding prompt - safe to call repeatedly,
  // it's a no-op for reports already synced (see spandanMigrationController.js).
  const handleSyncSpandanReports = async () => {
    try {
      setSyncingReports(true);
      const response = await syncSpandanReports();
      if (response?.success) {
        toast.success(response.message || "Reports synced successfully");
        setRefreshArg((prev) => !prev);
      } else {
        toast.error(response?.message || "Couldn't sync your reports right now");
      }
    } catch (err) {
      toast.error(err.message || "Couldn't sync your reports right now");
    } finally {
      setSyncingReports(false);
    }
  };

  const trackReportCardClick = (report, listPosition, mode) => {
    logAnalyticsEvent(ANALYTICS_EVENTS.ECG_REPORT_CARD_CLICKED, {
      report_id: getReportId(report),
      report_status: report?.report_status,
      interpretation_status: report?.interpretation_request_status,
      view_mode: mode,
      // Zero-based here; the index within the loaded (infinitely scrolled) list.
      list_position: listPosition,
    });
  };

  const handleReportSelect = (report, listPosition) => {
    const reportId = getReportId(report);
    if (!reportId) return;
    trackReportCardClick(report, listPosition, VIEW_MODE.CARD);
    setOpenedMap((current) => ({ ...current, [reportId]: true }));
    window.location.hash = reportId;
    if (isMobile) navigate(`/ecg-reports/${reportId}`, { state: report });
  };

  const handleViewReport = (report, listPosition) => {
    const reportId = getReportId(report);
    if (!reportId) return;
    // The list rows are not clickable; opening a report goes through the row's
    // action menu, so that is the list-view equivalent of a card click.
    trackReportCardClick(report, listPosition, VIEW_MODE.LIST);
    navigate(`/ecg-reports/${reportId}`, { state: report });
  };

  const downloadUrl = async (url, fileName) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("File request failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadReport = async (report) => {
    const reportId = getReportId(report);
    if (!reportId) return;

    try {
      const details = unwrapReportResponse(await fetcherGET(`${endpoints.ecg.getEcgDetails}${reportId}`));
      let reportFile = getPdfFile(details);

      if (!reportFile.url && !reportFile.base64) {
        const graph = unwrapReportResponse(await fetcherGET(`${endpoints.ecg.getEcgGraph}${reportId}`));
        reportFile = getPdfFile(graph);
      }

      const fileName = `${details?.collection_id || report?.collection_id || reportId}.pdf`;
      if (reportFile.url) await downloadUrl(reportFile.url, fileName);
      else if (reportFile.base64) {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${reportFile.base64}`;
        link.download = fileName;
        link.click();
      } else {
        toast.error("A downloadable report is not available yet.");
        return;
      }
      // Logged after the transfer starts, not on click — the early returns above
      // are the common case for reports whose PDF has not been generated yet.
      logAnalyticsEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
        report_type: REPORT_TYPE.ECG_REPORT,
        report_id: reportId,
      });
      toast.success("Report download started.");
    } catch (error) {
      console.error("Unable to download report:", error);
      toast.error("Unable to download the report. Please try again.");
    }
  };

  const handleDownloadAIReport = async (report) => {
    const reportId = getReportId(report);
    if (!reportId) return;

    try {
      const details = unwrapReportResponse(await fetcherGET(`${endpoints.ecg.getEcgDetails}${reportId}`));
      const graph = unwrapReportResponse(await fetcherGET(`${endpoints.ecg.getEcgGraph}${reportId}`));
      const aiReportUrl = getAIReportPdfUrl(graph) || getAIReportPdfUrl(details);

      if (!aiReportUrl) {
        toast.error("An AI generated report is not available yet.");
        return;
      }

      const fileName = `${details?.collection_id || report?.collection_id || reportId}_ai_generated.pdf`;
      await downloadUrl(aiReportUrl, fileName);
      logAnalyticsEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
        report_type: REPORT_TYPE.AI_REPORT,
        report_id: reportId,
      });
      toast.success("Report download started.");
    } catch (error) {
      console.error("Unable to download AI generated report:", error);
      toast.error("Unable to download the report. Please try again.");
    }
  };

  const handleOpenSendForInterpretation = async (report) => {
    setInterpretationSelected(null);
    setInterpretationNotes(null);
    setInterpretationReport(report);
    setInterpretationModalOpen(true);

    const reportId = getReportId(report);
    if (!reportId) return;

    try {
      const details = unwrapReportResponse(await fetcherGET(`${endpoints.ecg.getEcgDetails}${reportId}`));
      if (details && Object.keys(details).length) setInterpretationReport(details);
    } catch (error) {
      console.error("Unable to load report details:", error);
    }
  };

  const handleCloseSendForInterpretation = () => {
    setInterpretationModalOpen(false);
    setInterpretationReport(null);
  };

  const handleSendForInterpretation = async () => {
    const reportId = getReportId(interpretationReport);
    // The dialog stores the whole {value, userType} option rather than just the
    // id, so the interpreter bucket comes straight off the selection. Looking it
    // back up by id against interpretationItems never matched, which is why
    // interpreter_type was always absent. payment_mode and credits_charged have
    // no source on web — see the interpretation notes in src/analytics/events.js.
    const interpreterType = resolveInterpreterType(
      interpretationSelected?.userType
    );

    try {
      setInterpretationLoading(true);
      const res = await requestInterpretation({
        ids: [reportId],
        notes: interpretationNotes,
        sendTo: interpretationSelected,
        collectionId: interpretationReport?.collection_id,
      });
      if (res.success) {
        logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUESTED, {
          report_id: reportId,
          interpreter_type: interpreterType,
        });
        toast.success(res.message);
        handleCloseSendForInterpretation();
        setRefreshArg((current) => !current);
      } else {
        logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUEST_FAILED, {
          report_id: reportId,
          failure_reason: res.message,
        });
        toast.error(res.message);
      }
    } catch (error) {
      console.error("Unable to send report for interpretation:", error);
      logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUEST_FAILED, {
        report_id: reportId,
        failure_reason: "request_threw",
      });
      toast.error("Unable to send report for interpretation. Please try again.");
    } finally {
      setInterpretationLoading(false);
    }
  };

  const handleScroll = (event) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight < target.scrollHeight - 120 || isLoading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    setArgs((currentArgs) => ({ ...currentArgs, page: nextPage, limit: PAGE_SIZE }));
  };

  /* ------------------------------ CSV export ------------------------------ */
  /* The export used to flatten whatever the API returned, so the sheet carried
     raw JSON paths as headers ("patient_details.firstName") and epoch
     milliseconds as dates ("1791822432112"). The columns are declared here
     instead: a label people can read, and a getter that formats the value. */

  const IST = "Asia/Kolkata";

  // report_date / interpreted_date come from the API as real Dates; the legacy
  // epoch-millisecond fields are the fallback for rows that predate them.
  const fmtDate = (value, fallbackMs) => {
    // The legacy epoch fields are stored as STRINGS of milliseconds
    // ("1791810202731"), so they need an explicit Number() before moment sees them.
    const source = value ?? (fallbackMs ? Number(fallbackMs) : null);
    if (!source) return "";
    const m = moment(source);
    return m.isValid() ? m.tz(IST).format("YYYY-MM-DD HH:mm:ss") : "";
  };

  const fmtVital = (vital) =>
    vital?.value == null ? "" : `${vital.value}${vital.unit ? ` ${vital.unit}` : ""}`;

  const REPORT_TYPE_LABELS = {
    REPORT_TWELVE_LEAD: "12 Lead",
    REPORT_LEAD_TWO: "Lead II",
    REPORT_HRV: "HRV",
    REPORT_HYPERKALEMIA: "Hyperkalemia",
  };

  const EXPORT_COLUMNS = [
    { label: "Report ID", get: (r) => r.report?.id },
    { label: "Received On Server (IST)", get: (r) => fmtDate(r.report_date, r.report_timestamp) },
    { label: "Report Type", get: (r) => REPORT_TYPE_LABELS[r.report?.report_type] || r.report?.report_type },
    { label: "Patient Name", get: (r) => [r.patient_details?.firstName, r.patient_details?.lastName].filter(Boolean).join(" ") },
    { label: "Patient ID", get: (r) => r.patient_details?.patientId },
    { label: "Age", get: (r) => r.patient_details?.age },
    { label: "Gender", get: (r) => r.patient_details?.gender },
    { label: "Centre Name", get: (r) => r.center?.name },
    { label: "Centre ID", get: (r) => r.center?.id },
    { label: "Interpretation Status", get: (r) => r.interpretation_request_status },
    { label: "Result", get: (r) => r.interpretation_report_status },
    { label: "Classification of Abnormality", get: (r) => r.interpretation?.classification_of_abnormality },
    { label: "Interpretation Requested On (IST)", get: (r) => fmtDate(r.interpretation_requested_date) },
    { label: "Interpreted On (IST)", get: (r) => fmtDate(r.interpreted_date, r.interpretedAt) },
    { label: "Heart Rate (BPM)", get: (r) => r.interpretation?.characteristics?.bpm },
    { label: "PR Interval", get: (r) => r.interpretation?.characteristics?.pr },
    { label: "QRS Interval", get: (r) => r.interpretation?.characteristics?.qrs },
    { label: "QT Interval", get: (r) => r.interpretation?.characteristics?.qt },
    { label: "QTc", get: (r) => r.interpretation?.characteristics?.qtc },
    { label: "RR Interval", get: (r) => r.interpretation?.characteristics?.rr },
    { label: "Findings", get: (r) => r.interpretation?.findings },
    { label: "Suggested Actions", get: (r) => r.interpretation?.suggested_actions },
    { label: "Symptoms", get: (r) => r.collection?.symptoms },
    { label: "Medical History", get: (r) => r.collection?.medical_history_notes },
    { label: "Collected On (IST)", get: (r) => fmtDate(r.collection?.created_on) },
    { label: "Collection Status", get: (r) => r.collection?.status },
    { label: "Height", get: (r) => fmtVital(r.collection?.body_vitals?.height) },
    { label: "Weight", get: (r) => fmtVital(r.collection?.body_vitals?.weight) },
    { label: "Temperature", get: (r) => fmtVital(r.collection?.body_vitals?.temperature) },
    { label: "Blood Pressure", get: (r) => {
      const bp = r.collection?.body_vitals?.blood_pressure;
      return bp?.systolic == null && bp?.diastolic == null ? "" : `${bp?.systolic ?? ""}/${bp?.diastolic ?? ""} ${bp?.unit || "mmHg"}`;
    } },
    { label: "SpO2", get: (r) => fmtVital(r.collection?.body_vitals?.spo2) },
    { label: "Blood Sugar", get: (r) => fmtVital(r.collection?.body_vitals?.blood_sugar) },
    { label: "Respiratory Rate", get: (r) => fmtVital(r.collection?.body_vitals?.respiratory_rate) },
  ];

  const toCsvCell = (value) => {
    if (value == null) return '""';
    const text = Array.isArray(value) ? value.join(" | ") : String(value);
    return `"${text.replaceAll('"', '""')}"`;
  };

  const handleExport = async () => {
    try {
      const response = await reportsData({ ...args, page: 1, limit: 10000 });
      const exportReports = response?.ecgReport || [];
      if (!exportReports.length) return toast.error("No reports available to export.");

      const csv = [
        EXPORT_COLUMNS.map((column) => toCsvCell(column.label)).join(","),
        ...exportReports.map((report) =>
          EXPORT_COLUMNS.map((column) => {
            try {
              return toCsvCell(column.get(report));
            } catch {
              // One malformed row must not cost the user the whole export.
              return '""';
            }
          }).join(","),
        ),
      ].join("\n");

      // Excel needs the BOM to read the file as UTF-8; without it patient names
      // with non-ASCII characters open as mojibake.
      const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `ecg-reports-${moment().tz(IST).format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download failed", error);
      toast.error("Unable to export reports. Please try again.");
    }
  };

  const viewToggle = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={viewMode}
      onChange={(_, nextView) => {
        if (!nextView) return;
        sessionStorage.setItem(ECG_REPORTS_VIEW_MODE_KEY, nextView);
        setViewMode(nextView);
      }}
      aria-label="Report view"
      sx={{ height: 36, border: "1px solid var(--app-border)", borderRadius: 2, overflow: "hidden", backgroundColor: "var(--app-surface)", "& .MuiToggleButton-root": { width: 38, minWidth: 38, border: 0, p: 0, color: "var(--app-text-muted)" }, "& .Mui-selected": { backgroundColor: "var(--app-accent-soft) !important", color: "var(--app-accent) !important" } }}
    >
      <ToggleButton value="card" aria-label="Card view" title="Card view"><Icon icon="material-symbols:grid-view-rounded" width="18" height="18" /></ToggleButton>
      <ToggleButton value="list" aria-label="List view" title="List view"><Icon icon="material-symbols:view-list-rounded" width="19" height="19" /></ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <>
      <Helmet><title>Spandan - ECG Reports</title></Helmet>
      <div className="main-wrappers">
        <div className="page-wrapper" style={{ maxHeight: "99vh", overflowY: "hidden", paddingBottom: 10, fontFamily: "Figtree", backgroundColor: "var(--app-bg)" }}>
          <div className="content">
            {selectedCenter != null && all_centres.length === 0 && <Box className="alert alert-warning d-flex justify-content-between align-items-center"><Typography>You haven&apos;t added any centres to your organisation yet.</Typography><Link to="/add-centre">Add a Centre</Link></Box>}
            {isMigratedUser && (
              <Box
                sx={{
                  backgroundColor: "#F0F4FF",
                  border: "1px solid #C7D6FF",
                  borderRadius: "9px",
                  padding: "12px 14px",
                  marginBottom: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2A3B8F" }}>
                    Sync reports from your old Spandan account
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#2A3B8F", mt: 0.5 }}>
                    Bring over any ECG reports from your old Spandan account that aren&apos;t here yet.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  disabled={syncingReports}
                  onClick={handleSyncSpandanReports}
                  sx={{
                    textTransform: "none",
                    borderRadius: "9px",
                    backgroundColor: "#4F46E5",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#3F39B8", boxShadow: "none" },
                  }}
                >
                  {syncingReports ? "Syncing..." : "Sync my reports"}
                </Button>
              </Box>
            )}
            {autoSendPaused && (
              <Box
                sx={{
                  backgroundColor: "#FFFBF0",
                  border: "1px solid #F4D88A",
                  borderRadius: "9px",
                  padding: "12px 14px",
                  marginBottom: "12px",
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#8B6200" }}>
                  Auto-send is paused because you have 0 credits.
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "#8B6200", mt: 0.5 }}>
                  New reports will not be sent for interpretation until credits are added. Reports skipped
                  while auto-send was paused are marked{" "}
                  <strong>Skipped — no credits</strong> and need to be sent manually; they are not queued
                  or sent automatically later.{" "}
                  {user?.userType === "b2b" ? (
                    <>
                      <Link to="/settings/credits" style={{ color: "#6744ff", fontWeight: 600 }}>
                        Top up credits
                      </Link>{" "}
                      to resume.
                    </>
                  ) : (
                    <>
                      <strong>
                        Top up credits
                      </strong>{" "}
                      to resume.
                    </>
                  )}
                </Typography>
              </Box>
            )}
            <Typography variant="h6" sx={{ fontFamily: "Figtree", fontSize: 20, fontWeight: 700 }}>ECG Reports</Typography>
            <ReportSearch onSearch={handleSearch} />
            <CustomFilters
              component="ecg"
              onApply={applyFilters}
              rightContent={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Typography sx={{ color: "var(--app-text-muted)", fontSize: 12, fontWeight: 700 }}>View</Typography>{viewToggle}</Box>}
            />

            {showEmptyReports ? (
              <BlankPreview sx={{ minHeight: "65vh", mt: 2 }} icon={nopreview} title="No Report Available" />
            ) : viewMode === "list" ? (
              <ReportsListView reports={reports} isLoading={isLoading} totalCount={totalCount} hasMore={hasMore} listRef={listRef} onScroll={handleScroll} openedMap={openedMap} onExport={handleExport} onViewReport={handleViewReport} onDownloadReport={handleDownloadReport} canDownloadAIReport={canViewAIgeneratedReports()} onDownloadAIReport={handleDownloadAIReport} onSendForInterpretation={handleOpenSendForInterpretation} isMigratedUser={isMigratedUser} />
            ) : (
              <div className="row" style={{ marginTop: 20 }}>
                <ReportsCardView reports={reports} isLoading={isLoading} totalCount={totalCount} hasMore={hasMore} listRef={listRef} onScroll={handleScroll} onSelectReport={handleReportSelect} selectedReportId={hashId} openedMap={openedMap} onExport={handleExport} />
                <div className="col-md-8 d-none d-md-block">
                  {selectedReport || hashId ? <ReportPreview type="short" data={selectedReport} hasId={hashId} Link={Link} handleRefreshList={() => setRefreshArg((current) => !current)} reportData={reports.length} /> : <BlankPreview sx={{ minHeight: "65vh" }} icon={nopreview} title="No Preview Available" subtitle="Select a report to preview" />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SendForInterpretationDialog
        open={interpretationModalOpen}
        onClose={handleCloseSendForInterpretation}
        EcgReport={interpretationReport}
        items={interpretationItems}
        selected={interpretationSelected}
        setSelected={setInterpretationSelected}
        Notes={interpretationNotes}
        setNotes={setInterpretationNotes}
        buttonLoading={interpretationLoading}
        handleSendForInterpretation={handleSendForInterpretation}
      />
    </>
  );
}

function ReportSearch({ onSearch }) {
  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        placeholder="Search by Report ID, Patient ID, Collection ID, Patient Name"
        onChange={(event) => onSearch(event.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "var(--app-text-muted)" }} /></InputAdornment> }}
        sx={{ "& .MuiOutlinedInput-root": { height: 45, borderRadius: "9px", backgroundColor: "var(--app-surface)", "& fieldset": { borderColor: "var(--app-border)" } } }}
      />
    </Box>
  );
}

ReportSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

export default EcgReportView;
